import * as React from "react";
import { useEffect, useRef, useState } from "react";
import styles from "./appbar.module.css";
import { useNavigate } from "react-router-dom";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Menu from "@mui/material/Menu";
import MenuIcon from "@mui/icons-material/Menu";
import Container from "@mui/material/Container";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import MenuItem from "@mui/material/MenuItem";
import useAuth from "../../hooks/useAuth";
import { headItems } from "./headItems";
import { MdNotifications } from "react-icons/md";
import {
  determineBackgroundColor,
  determineColor,
} from "../../utils/determine";
import { ARTIST_NEXT_DOOR_LOGO } from "../../utils/images";
import StyledButton from "../Buttons/StyledButton";
import DashboardButton from "./mydashboardButton";
import NotificationModal from "../notificationModal";
import Expandablemenu from "./ExpandableMenuItem";

function EnhancedResponsiveAppBar({ activeSection, renderHeader = false }) {
  const navigate = useNavigate();
  const { isAuthenticated, logout, user } = useAuth();
  const appBarRef = useRef(null);

  // Navigation states
  const [theColor, setTheColor] = useState("white");
  const [navbarPosition, setNavbarPosition] = useState(0);
  const [footerNavOpacity, setFooterNavOpacity] = useState(1);
  const [backgroundColor, setBackgroundColor] = useState("transparent");
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [hasNewNotification, setHasNewNotification] = useState(true);
  const [pulseAnimation, setPulseAnimation] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [activeMenuItem, setActiveMenuItem] = useState("");
  const hasAnimated = useRef(true);
  const notificationRef = useRef(null);
  const lastScrollY = useRef(0);

  // MUI menu states
  const [anchorElNav, setAnchorElNav] = useState(null);
  const [anchorElUser, setAnchorElUser] = useState(null);

  // Extract pages and settings from headItems
  const pages = headItems.filter(
    (item) => item.isMenuItem && item.name !== "Login"
  );
  // todo
  // if (user?.type === "user" || user?.type === "artist") {
  //   pages.push({
  //     key: "jobs",
  //     name: "Find/Post Jobs",
  //     link: "/jobs",
  //     isMenuItem: true,
  //     fullLink: "/jobs",
  //   });
  // }
  if (user?.type === "user") {
    pages.push({
      key: "dashboard",
      name: "My dashboard",
      link: "/dashboard/user",
      isMenuItem: true,
      fullLink: "/dashboard/user",
    });
  } else if (user?.type === "artist") {
    pages.push({
      key: "dashboard",
      name: "My dashboard",
      link: "/dashboard/artist",
      isMenuItem: true,
      fullLink: "/dashboard/user",
    });
  }
  // pages.push({
  //   key: "jobs",
  //   name: "Jobs",
  //   link: "/jobs",
  //   isMenuItem: true,
  //   fullLink: "/jobs",
  // });

  const settings = headItems
    .filter((item) => item.isDropdown && item.key === "user")
    .flatMap((item) => item.children || []);

  // Handle menu operations
  const handleOpenNavMenu = (event) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  // Navigation handlers
  const handleNavigation = (link, key) => {
    navigate(link);
    setActiveMenuItem(key);
    handleCloseNavMenu();
  };

  // Handle user menu selection including logout
  const handleSettingClick = (setting) => {
    if (setting.name === "Logout") {
      logout();
    } else if (setting.url) {
      navigate(setting.url);
    }
    handleCloseUserMenu();
  };

  // Monitor scroll position for AppBar effect
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // Determine scroll direction and distance
      if (currentScrollY > 30) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }

      // Show/hide based on scroll direction
      if (currentScrollY > lastScrollY.current && currentScrollY > 100) {
        setHidden(true); // Scrolling down
      } else {
        setHidden(false); // Scrolling up
      }

      lastScrollY.current = currentScrollY;
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Set active menu item based on current path
  useEffect(() => {
    const path = window.location.pathname;
    const active = pages.find((page) => path.includes(page.link));
    if (active) {
      setActiveMenuItem(active.key);
    }
  }, [pages]);

  // Add pulse animation periodically if there are new notifications
  useEffect(() => {
    let intervalId;

    if (hasNewNotification && !notificationsOpen) {
      intervalId = setInterval(() => {
        setPulseAnimation(true);
        setTimeout(() => setPulseAnimation(false), 1000);
      }, 5000);
    }

    return () => {
      clearInterval(intervalId);
    };
  }, [hasNewNotification, notificationsOpen]);

  // Close notification modal when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        notificationRef.current &&
        !notificationRef.current.contains(event.target)
      ) {
        setNotificationsOpen(false);
      }
    }

    if (notificationsOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [notificationsOpen]);

  // Effects for appearance based on active section
  useEffect(() => {
    setTheColor(determineColor(activeSection));
    setBackgroundColor(determineBackgroundColor(activeSection));

    if (activeSection === "footer") {
      setNavbarPosition("-100%");
      setFooterNavOpacity(0);
    } else {
      setNavbarPosition(0);
      setFooterNavOpacity(1);
    }
  }, [activeSection]);

  useEffect(() => {
    if (hasAnimated.current) {
      hasAnimated.current = false;
    }
  }, []);

  // Handle notification click
  const handleNotificationClick = () => {
    setPulseAnimation(false);
    setNotificationsOpen(!notificationsOpen);
    if (hasNewNotification && !notificationsOpen) {
      setHasNewNotification(false);
    }
  };

  return (
    <AppBar
      ref={appBarRef}
      position="fixed"
      className={`${styles.appBar} ${scrolled ? styles.scrolled : ""} ${
        hidden ? styles.hidden : ""
      }`}
      sx={{
        background: scrolled ? "rgba(0, 0, 0, 0.95)" : "rgba(0, 0, 0, 0.7)",
        transform: hidden
          ? "translateY(-100%)"
          : `translateY(${navbarPosition}px)`,
        opacity: footerNavOpacity,
        transition: "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
        backdropFilter: "blur(10px)",
        boxShadow: scrolled ? "0 4px 30px rgba(0, 0, 0, 0.1)" : "none",
      }}
    >
      <Container maxWidth="xl">
        <Toolbar disableGutters className={styles.toolbar}>
          {/* Logo for desktop */}
          <Box
            component="div"
            className={styles.logoContainer}
            onClick={() => navigate("/")}
          >
            <Box
              component="img"
              src={"/logo.jpeg"}
              alt="Artist Next Door"
              className={styles.logoImage}
              sx={{
                display: { xs: "none", md: "flex", borderRadius: "50%" },
                mr: 1,
                height: 50,
                cursor: "pointer",
                transition: "transform 0.2s ease",
                "&:hover": {
                  transform: "scale(1.2)",
                },
              }}
            />
            <Typography
              variant="h6"
              className={styles.brandName}
              sx={{
                display: { xs: "none", md: "flex" },
                fontFamily: "Nunito, sans-serif",
                fontWeight: 700,
                letterSpacing: ".1rem",
                color: "white",
                textDecoration: "none",
                marginLeft: "10px",
              }}
            >
              ArtistNextDoor
            </Typography>
          </Box>

          {/* Mobile menu */}
          <Box sx={{ flexGrow: 1, display: { xs: "flex", md: "none" } }}>
            <IconButton
              size="large"
              aria-label="navigation menu"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleOpenNavMenu}
              color="inherit"
              className={styles.menuButton}
            >
              <MenuIcon sx={{ color: theColor }} />
            </IconButton>
            <Box
              component="img"
              src={"/logo.jpeg"}
              alt="Artist Next Door"
              sx={{
                display: {
                  xs: "block",
                  marginLeft: "20px",
                  md: "none",
                  borderRadius: "50%",
                  marginTop: "4px",
                },
                height: 35,
                cursor: "pointer",
                transition: "transform 0.2s ease",
                "&:hover": {
                  transform: "scale(1.2)",
                },
              }}
              onClick={() => navigate("/")}
            />
            <Menu
              id="menu-appbar"
              anchorEl={anchorElNav}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
              keepMounted
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
              className={styles.mobileMenu}
              sx={{ display: { xs: "block", md: "none" } }}
              PaperProps={{
                className: styles.menuPaper,
              }}
            >
              {pages.map((page) => (
                <>
                  {page.fullLink !== "/jobs" ? (
                    <MenuItem
                      key={page.key}
                      onClick={() => handleNavigation(page.link, page.key)}
                      className={`${styles.menuItem} ${
                        activeMenuItem === page.key ? styles.activeMenuItem : ""
                      }`}
                    >
                      <Typography
                        sx={{
                          textAlign: "center",
                          fontWeight: 700,
                          color: "white",
                        }}
                      >
                        {page.name}
                      </Typography>
                    </MenuItem>
                  ) : (
                    <Expandablemenu />
                  )}
                </>
              ))}
            </Menu>
          </Box>

          {/* Desktop menu */}
          <Box
            sx={{
              flexGrow: 1,
              alignItems: "center",
              display: { xs: "none", md: "flex" },
              justifyContent: "center",
            }}
            className={styles.desktopMenu}
          >
            {pages.map((page) => (
              <React.Fragment key={page.key}>
                {page.key !== "dashboard" && page.key !== "jobs" ? (
                  <Button
                    onClick={() => handleNavigation(page.link, page.key)}
                    className={`${styles.navButton} ${
                      activeMenuItem === page.key ? styles.activeNavButton : ""
                    }`}
                    sx={{
                      my: 2,
                      color: theColor,
                      display: "block",
                      fontWeight: 700,
                      fontFamily: "Nunito",
                      marginX: "8px",
                      position: "relative",
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        bottom: "10px",
                        left: "50%",
                        width: activeMenuItem === page.key ? "50%" : "0%",
                        height: "2px",
                        background: "#fff",
                        transition: "all 0.3s ease",
                        transform: "translateX(-50%)",
                      },
                      "&:hover::after": {
                        width: "50%",
                      },
                    }}
                  >
                    {page.name.toLowerCase()}
                  </Button>
                ) : page.key !== "jobs" ? (
                  <DashboardButton
                    redirect={page.link}
                    className={`${styles.dashboardButton} ${
                      activeMenuItem === page.key
                        ? styles.activeDashboardButton
                        : ""
                    }`}
                  />
                ) : (
                  <Expandablemenu />
                )}
              </React.Fragment>
            ))}
          </Box>

          {/* User menu and notifications */}
          <Box
            sx={{
              flexGrow: 0,
              display: "flex",
              alignItems: "center",
            }}
            className={styles.userSection}
          >
            {isAuthenticated ? (
              <>
                <div
                  className={styles.userControls}
                  style={{
                    display: "flex",
                    gap: "24px",
                    alignItems: "center",
                  }}
                >
                  <div
                    className={styles.notificationContainer}
                    ref={notificationRef}
                    onMouseEnter={() => setIsHovering(true)}
                    onMouseLeave={() => setIsHovering(false)}
                  >
                    <div
                      className={`${styles.notificationIconWrapper} ${
                        pulseAnimation ? styles.pulse : ""
                      } ${isHovering ? styles.notificationHover : ""}`}
                      onClick={handleNotificationClick}
                    >
                      <MdNotifications
                        className={styles.notificationIcon}
                        color="white"
                        size={30}
                      />

                      {hasNewNotification && (
                        <span className={styles.notificationBadge}>
                          <span className={styles.badgePulse}></span>
                        </span>
                      )}
                    </div>

                    {notificationsOpen && (
                      <div className={styles.animatedBox}>
                        <NotificationModal
                          setNotificationsOpen={setNotificationsOpen}
                        />
                      </div>
                    )}
                  </div>

                  <Tooltip
                    title={user?.username}
                    classes={{ tooltip: styles.userTooltip }}
                  >
                    <IconButton
                      onClick={handleOpenUserMenu}
                      sx={{ p: 0 }}
                      className={styles.userButton}
                    >
                      <div
                        className={styles.userAvatar}
                        style={{
                          color: "#fff",
                          backgroundColor: "black",
                          padding: "16px",
                          borderRadius: "50%",
                          height: "10px",
                          width: "10px",
                          textAlign: "center",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          border: "2px solid white",
                          boxShadow: "0 0 10px rgba(255, 255, 255, 0.3)",
                          transition: "all 0.3s ease",
                        }}
                      >
                        {user?.username.charAt(0).toUpperCase()}
                      </div>
                    </IconButton>
                  </Tooltip>
                  <Menu
                    sx={{ mt: "45px" }}
                    id="menu-appbar"
                    anchorEl={anchorElUser}
                    anchorOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                    keepMounted
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                    open={Boolean(anchorElUser)}
                    onClose={handleCloseUserMenu}
                    className={styles.userMenu}
                    PaperProps={{
                      className: styles.userMenuPaper,
                    }}
                  >
                    {settings.map((setting) => (
                      <MenuItem
                        key={setting.key}
                        onClick={() => handleSettingClick(setting)}
                        className={styles.userMenuItem}
                      >
                        <Typography sx={{ textAlign: "center" }}>
                          {setting.name}
                        </Typography>
                      </MenuItem>
                    ))}
                    <MenuItem
                      onClick={() => {
                        logout();
                        handleCloseUserMenu();
                      }}
                      className={styles.logoutMenuItem}
                    >
                      <Typography sx={{ textAlign: "center", color: "white" }}>
                        Logout
                      </Typography>
                    </MenuItem>
                  </Menu>
                </div>
              </>
            ) : (
              <StyledButton
                label={"LOGIN"}
                onClick={() => {
                  navigate("/login");
                }}
                className={styles.loginButton}
              />
            )}
          </Box>
        </Toolbar>
      </Container>

      {/* Animated glow effect */}
      <div className={styles.appBarGlow}></div>
    </AppBar>
  );
}

export default EnhancedResponsiveAppBar;
